import depsloader from "../../../depsloader";

const REQUEST_TIMEOUT = 20000;

function geocoderConfig() {
  const cfg = typeof window !== "undefined" ? window.swGeocoder : null;
  if (!cfg || typeof cfg.key !== "string" || cfg.key.trim() === "") {
    return null;
  }
  if (typeof cfg.searchUrl !== "string" || typeof cfg.reverseUrl !== "string") {
    return null;
  }

  return cfg;
}

function cancelledError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

function isCancelled(err) {
  return !!(err && err.cancelled);
}

function finiteNumber(v) {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return typeof n === "number" && isFinite(n) ? n : null;
}

function nonEmpty(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    const request = this.axiosRequest;
    this.axiosRequest = null;
    if (request != null) {
      request.source.cancel();
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (!p.length && g.name) {
      p.push(g.name);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature von api.screenwork.de/geocoder in das bisherige
  // geoaddress-Format um, ohne das Feature zu verändern.
  _geoaddress(feature) {
    if (!feature || !feature.geometry || !Array.isArray(feature.geometry.coordinates)) {
      return null;
    }

    const lng = finiteNumber(feature.geometry.coordinates[0]);
    const lat = finiteNumber(feature.geometry.coordinates[1]);
    if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const p = feature.properties || {};
    const type = nonEmpty(p.type);
    const name = nonEmpty(p.name);

    let street = nonEmpty(p.street);
    let number = nonEmpty(p.housenumber);
    let city = nonEmpty(p.city);
    let district = nonEmpty(p.district);

    if (type) {
      if (type === "street" && !street) {
        street = name;
      } else if (type === "city" && !city) {
        city = name;
      } else if (type === "district" && name) {
        district = name;
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type
      if (p.osm_key === "highway" && !street) {
        street = name;
      } else if (p.osm_key === "boundary" || (p.osm_key === "place" && p.osm_value === "suburb")) {
        if (name && city && name !== city) {
          district = district || name;
        } else if (!city) {
          city = name;
        }
      } else if (p.osm_key === "place" && !city) {
        city = name;
      }
    }

    const geoaddress = {};

    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    if (nonEmpty(p.postcode)) {
      geoaddress["zip"] = p.postcode;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district && district !== city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText({ ...geoaddress, name });

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _results(data) {
    const results = [];
    const features = data && Array.isArray(data.features) ? data.features : [];

    features.forEach(feature => {
      const geoaddress = this._geoaddress(feature);
      if (geoaddress) {
        results.push(geoaddress);
      }
    });

    return results;
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const cfg = geocoderConfig();
    if (!query || !cfg) {
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    const request = { source };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("layer", "city");
    params.append("layer", "district");
    params.append("key", cfg.key);

    return this.axios.get(cfg.searchUrl, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (this.axiosRequest !== request) {
        throw cancelledError();
      }

      return this._results(res.data);
    }, err => {
      if (this.axios.isCancel(err) || this.axiosRequest !== request) {
        throw cancelledError();
      }

      throw err;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    const latitude = finiteNumber(lat);
    const longitude = finiteNumber(lng);
    if (latitude === null || longitude === null || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const cfg = geocoderConfig();
    if (!cfg) {
      return Promise.resolve(null);
    }

    const params = new URLSearchParams();
    params.append("lat", String(latitude));
    params.append("lon", String(longitude));
    params.append("lang", "de");
    params.append("key", cfg.key);

    return this.axios.get(cfg.reverseUrl, {
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const results = this._results(res.data);
      return results.length > 0 ? results[0] : null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory, isCancelled };

export default {};
